// Statistics — queue statistics table.
//
// Every presented queue (analyze run or bulk import) is a numbered batch:
// identified by its creation date/time and a consecutive sequence number.
// Fully rated batches (no member left on the queue) are scored on their
// watched subset: count per rating category 1–9 and the average rating.
// Batches still being worked through are listed separately with progress.

import { Link } from "react-router-dom";

import { useQueueStats } from "../api/hooks";
import { RATING_LABELS, type QueueStat } from "../api/types";

import "./Statistics.css";

const RATINGS = [1, 2, 3, 4, 5, 6, 7, 8, 9] as const;

function fmtDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function Statistics() {
  const { data, error } = useQueueStats();

  const queues = data?.queues ?? [];
  const complete = queues.filter((q) => q.complete);
  const open = queues.filter((q) => !q.complete);

  return (
    <div className="stats">
      <div className="stats__inner">
        <div className="stats__head">
          <h1 className="stats__title">queue statistics</h1>
          <Link to="/" className="stats__back">
            ← back to navigator
          </Link>
        </div>

        <p className="stats__blurb">
          every presented queue is a numbered batch — identified by its creation time and a
          consecutive number. once a batch is fully rated, its watched titles are scored:
          how many landed in each rating category and how well the batch did on average.
        </p>

        {error ? (
          <div className="stats__empty">could not load statistics — try reloading.</div>
        ) : data == null ? (
          <div className="stats__empty">loading…</div>
        ) : complete.length === 0 ? (
          <div className="stats__empty">
            no fully rated queues yet — finish rating a queue and its statistics appear here.
          </div>
        ) : (
          <section className="stats__section">
            <h2 className="stats__section-title">fully rated queues</h2>
            <div className="stats__scroller">
              <table className="stats__table">
                <thead>
                  <tr>
                    <th className="stats__th stats__th--left">queue</th>
                    <th className="stats__th stats__th--left">created</th>
                    <th className="stats__th">titles</th>
                    <th className="stats__th">watched</th>
                    {RATINGS.map((n) => (
                      <th key={n} className="stats__th stats__th--rating" title={RATING_LABELS[n]}>
                        {n}
                      </th>
                    ))}
                    <th className="stats__th" title="watched without a rating">
                      unrated
                    </th>
                    <th className="stats__th">Ø rating</th>
                  </tr>
                </thead>
                <tbody>
                  {complete.map((q) => (
                    <QueueRow key={q.seq} q={q} />
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        )}

        {open.length > 0 && (
          <section className="stats__section stats__section--open">
            <h2 className="stats__section-title">queues in progress</h2>
            <ul className="stats__open-list">
              {open.map((q) => (
                <li key={q.seq} className="stats__open-row">
                  <span className="stats__open-id">#{q.seq}</span>
                  <span className="stats__open-date">{fmtDate(q.created_at)}</span>
                  <span className="stats__open-progress">
                    {q.size - q.pending} of {q.size} handled — {q.pending} still on the queue
                  </span>
                </li>
              ))}
            </ul>
          </section>
        )}
      </div>
    </div>
  );
}

function QueueRow({ q }: { q: QueueStat }) {
  return (
    <tr className="stats__tr">
      <td className="stats__td stats__td--left stats__td--id">#{q.seq}</td>
      <td className="stats__td stats__td--left stats__td--date">{fmtDate(q.created_at)}</td>
      <td className="stats__td">{q.size}</td>
      <td className="stats__td">{q.watched}</td>
      {RATINGS.map((n) => {
        const count = q.rating_counts[n] ?? 0;
        return (
          <td
            key={n}
            className={"stats__td stats__td--rating" + (count === 0 ? " stats__td--zero" : "")}
          >
            {count === 0 ? "·" : count}
          </td>
        );
      })}
      <td className={"stats__td" + (q.unrated === 0 ? " stats__td--zero" : "")}>
        {q.unrated === 0 ? "·" : q.unrated}
      </td>
      <td className="stats__td stats__td--avg">
        {q.avg_rating == null ? "—" : q.avg_rating.toFixed(2)}
      </td>
    </tr>
  );
}
